import type { AdminV2FeatureAccessEnvironment } from "@/types/adminV2";

/**
 * The sites the Feature flags console manages, in display order.
 *
 * @remarks
 * Every deployment renders both columns; the API answers for the site it can
 * actually manage and refuses the rest (the staging deployment refuses
 * production targets, and production without a configured staging gateway
 * refuses staging targets). The console renders those refusals as an
 * unavailable state instead of hiding the column, so operators can see that
 * the other site exists and where to manage it.
 */
export const FEATURE_ACCESS_UI_ENVIRONMENTS: ReadonlyArray<{
  key: AdminV2FeatureAccessEnvironment;
  label: string;
}> = [
  { key: "production", label: "Production" },
  { key: "staging", label: "Staging" },
];

export const featureAccessEnvironmentLabel = (
  environment: AdminV2FeatureAccessEnvironment,
): string =>
  FEATURE_ACCESS_UI_ENVIRONMENTS.find((entry) => entry.key === environment)
    ?.label ?? environment;

/**
 * Why an environment column is unavailable, derived from the API's refusal
 * codes; `null` for ordinary errors (which render as the usual failure state).
 */
export type FeatureAccessEnvironmentUnavailability =
  "not_allowed" | "gateway_unconfigured";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Maps a query/mutation error to an environment-unavailability reason.
 *
 * @remarks
 * The API refuses cross-site requests with stable public error codes:
 * `environment_not_allowed` (this deployment may never manage that site — the
 * staging admin asking for production) and `staging_gateway_unconfigured`
 * (production without the staging gateway env var). Anything else is a normal
 * transient error.
 */
export const resolveFeatureAccessEnvironmentUnavailability = (
  error: unknown,
): FeatureAccessEnvironmentUnavailability | null => {
  if (!isRecord(error)) {
    return null;
  }
  // The admin API client surfaces public errors axios-style
  // (`error.response.data`); tolerate flatter `error.data`/`error` shapes too.
  const response = isRecord(error.response) ? error.response : error;
  const data = isRecord(response.data) ? response.data : response;
  const errorCode =
    typeof data.errorCode === "string" ? data.errorCode : undefined;
  if (errorCode === "environment_not_allowed") {
    return "not_allowed";
  }
  if (errorCode === "staging_gateway_unconfigured") {
    return "gateway_unconfigured";
  }
  return null;
};

export const formatEnvironmentUnavailabilityHint = (
  unavailability: FeatureAccessEnvironmentUnavailability,
): string =>
  unavailability === "not_allowed"
    ? "Managed from the production admin"
    : "Staging gateway not configured";
